import React from 'react';
import { Tooltip } from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';

// Opacity-only fade for the tooltip.
//
// We keep Recharts' own animation off (isAnimationActive={false}) because it
// animates the tooltip's POSITION — the box slides across the chart chasing the
// cursor, which reads as laggy. Instead we fade purely on opacity so the box
// appears in place and eases in/out.
//
// Recharts toggles the wrapper between `visibility: hidden` (inactive) and
// `visibility: visible` (active) rather than changing opacity, so a bare
// `transition: opacity` never fires. We drive opacity off that visibility state
// via an attribute selector, and give `visibility` a matching transition-delay
// so the element stays rendered through the fade-out before hiding.
//
// prefers-reduced-motion users get no transition at all (instant), which is the
// correct behavior for a cursor-tracking element.
const FADE_STYLE_ID = 'themed-tooltip-fade';
if (typeof document !== 'undefined' && !document.getElementById(FADE_STYLE_ID)) {
  const styleEl = document.createElement('style');
  styleEl.id = FADE_STYLE_ID;
  styleEl.textContent = `
    .recharts-tooltip-wrapper {
      opacity: 1;
      transition: opacity 140ms ease-out;
    }
    .recharts-tooltip-wrapper[style*="visibility: hidden"] {
      opacity: 0;
      transition: opacity 140ms ease-out, visibility 0s linear 140ms;
    }
    @media (prefers-reduced-motion: reduce) {
      .recharts-tooltip-wrapper,
      .recharts-tooltip-wrapper[style*="visibility: hidden"] {
        transition: none;
      }
    }
  `;
  document.head.appendChild(styleEl);
}

// Drop-in replacement for Recharts' <Tooltip/> that follows the app's light/dark
// theme. Passes through all props so callers can still supply `formatter`,
// `content`, `cursor`, etc.; supplied `contentStyle` / `itemStyle` / `labelStyle`
// are merged on top of the themed defaults.
const DARK_STYLES = {
  contentStyle: {
    backgroundColor: 'rgba(17, 24, 39, 0.95)',
    border: '1px solid rgba(75, 85, 99, 0.5)',
    borderRadius: 8,
    color: '#f3f4f6',
    fontSize: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    padding: '8px 10px',
  },
  itemStyle: { color: '#f3f4f6' },
  labelStyle: { color: '#f3f4f6', fontWeight: 600 },
  cursor: { stroke: 'rgba(156,163,175,0.25)', fill: 'rgba(255,255,255,0.04)' },
};

const LIGHT_STYLES = {
  contentStyle: {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    border: '1px solid rgba(209, 213, 219, 0.8)',
    borderRadius: 8,
    color: '#111827',
    fontSize: 12,
    boxShadow: '0 8px 24px rgba(17,24,39,0.08)',
    padding: '8px 10px',
  },
  itemStyle: { color: '#111827' },
  labelStyle: { color: '#111827', fontWeight: 600 },
  cursor: { stroke: 'rgba(107,114,128,0.25)', fill: 'rgba(17,24,39,0.04)' },
};

// Themed surface for custom Recharts `content={({...}) => ...}` tooltips.
// Use when you need a custom JSX layout but still want light/dark-aware chrome.
// Usage:
//   content={({ active, payload, label }) => active && payload?.length ? (
//     <ThemedTooltipBox title={label}>...rows...</ThemedTooltipBox>
//   ) : null}
export function ThemedTooltipBox({ title, children }) {
  const { isDarkMode } = useTheme();
  const base = isDarkMode ? DARK_STYLES.contentStyle : LIGHT_STYLES.contentStyle;
  return (
    <div style={base}>
      {title != null && <div style={{ fontWeight: 600, marginBottom: 4 }}>{title}</div>}
      {children}
    </div>
  );
}

export default function ThemedChartTooltip({
  contentStyle,
  itemStyle,
  labelStyle,
  wrapperStyle,
  cursor,
  separator = ': ',
  ...rest
}) {
  const { isDarkMode } = useTheme();
  const base = isDarkMode ? DARK_STYLES : LIGHT_STYLES;
  return (
    <Tooltip
      // Disable Recharts' built-in animation (it slides the box's POSITION across
      // the chart, which reads as "flying in from the left"). The gentle
      // opacity-only fade is handled instead by the injected CSS above. Still
      // overridable via `rest` if a caller ever wants Recharts' animation back.
      isAnimationActive={false}
      contentStyle={{ ...base.contentStyle, ...contentStyle }}
      itemStyle={{ ...base.itemStyle, ...itemStyle }}
      labelStyle={{ ...base.labelStyle, ...labelStyle }}
      cursor={cursor !== undefined ? cursor : base.cursor}
      wrapperStyle={{ zIndex: 9999, outline: 'none', ...wrapperStyle }}
      separator={separator}
      {...rest}
    />
  );
}
